/*
MainPage.js
主页面
 */

import React, { useState, useEffect, useRef } from 'react';
import { Layout } from 'antd';
import { useXAgent, useXChat } from '@ant-design/x';

import './mainPage_styles.css'
import SiderMenu from "./SiderMenu/SiderMenu";
import HeaderMenu from "./HeaderMenu/HeaderMenu";
import FooterView from "./FooterView/FooterView";
import ContentView from "./ContentView/ContentView";
import MobileSiderDrawer from "./SiderMenu/MobileSiderDrawer/MobileSiderDrawer";
import {
    addSessionMessages,
    getSessionMessages, getSessionSuggestions,
    initDB,
    updateSessionSuggestions,
    updateSessionTitle
} from "../Model/IndexedDB";
import {
    ACTIVE_SESSION_DEFAULT_VALUE,
    ACTIVE_SESSION_KEY,
    USER_DEFAULT_VALUE,
    USER_KEY,
    DefaultURL, POINT5_USER, POINT5_ASSISTANT
} from "../Common/constants";
import { SUGGESTION_DEFAULT_VALUE_1, SUGGESTION_DEFAULT_VALUE_2, SUGGESTION_DEFAULT_VALUE_3 } from "../Common/constants";
import getQuestions from "../NetComponents/getQuestions";
import getSessionNewTitle from "../NetComponents/getSessionNewTitle";
import { initDoubleIncCards } from "../Model/DoubleIncIndexedDB/DoubleIncIndexedDB";


const { Header, Content, Footer, Sider } = Layout;

const MainPage = () => {
    // 是否匹配是移动端
    const [isMobile, setIsMobile] = useState(false);
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    // 拓展的三个问题
    const [suggestions, setSuggestions] =
        useState([SUGGESTION_DEFAULT_VALUE_1, SUGGESTION_DEFAULT_VALUE_2, SUGGESTION_DEFAULT_VALUE_3]);
    // 终端对话
    const abortChatRef = useRef(() => {
    });
    // 更新了会话的标题
    const [newSessionTitle, setNewSessionTitle] = useState('');
    // 历史会话
    const historyMsgsRef = useRef([]); // 使用 useRef 保存变量
    // contentView是否进行滚动的判断
    const [willScroll, setWillScroll] = useState(false);


    // 计算窗口大小
    useEffect(() => {
        // 计算视口高度
        const setFullHeight = () => {
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            // 更新宽度和高度
            document.documentElement.style.setProperty('--vh', `${viewportHeight / 100}px`);
            document.documentElement.style.setProperty('--vw', `${viewportWidth / 100}px`);
        };

        setFullHeight(); // 初次加载时设置
        window.addEventListener("resize", setFullHeight); // 页面大小改变时重新计算

        return () => {
            window.removeEventListener("resize", setFullHeight);
        };
    }, []);

    // 初始化
    useEffect(() => {
        // 初始化活动的session
        sessionStorage.setItem(ACTIVE_SESSION_KEY, ACTIVE_SESSION_DEFAULT_VALUE);
        // 初始化用户
        sessionStorage.setItem(USER_KEY, USER_DEFAULT_VALUE);
        // 初始化会话与双增公式数据库
        initDB().then((db) => {
            initDoubleIncCards(db).then();
        });
        // 初始化，即刷新页面，设置需要进行滚动
        setWillScroll(true);
    }, []);

    // 确保在组件卸载时取消正在进行的流式数据读取操作。
    // 因为如果组件在流的读取过程中被卸载，流的读取操作仍可能在后台执行，从而导致内存泄漏或无意义的后台任务
    useEffect(() => {
        return () => {
            abortChatRef.current();
        };
    }, []);

    // 在收起侧边栏的情况下，展示drawer
    const showDrawer = () => {
        setDrawerOpen(true);
    };

    // 点击drawer的关闭按钮
    const drawerOnClose = () => {
        setDrawerOpen(false);
    };

    // Agent for request
    const [agent] = useXAgent({
        request: async ({ message }, { onSuccess, onUpdate, onError }) => {
            const activeSessionID = sessionStorage.getItem(ACTIVE_SESSION_KEY);
            try {
                // 先检查并更新一下会话的标题
                const sessionID = sessionStorage.getItem(ACTIVE_SESSION_KEY)
                console.log("已激活的sessionID:", sessionID);
                getSessionMessages(sessionID).then((msgs) => {
                    if (msgs.length === 1) {
                        getSessionNewTitle({ query: message }).then((title) => {
                            updateSessionTitle(sessionID, title).then((title) => {
                                setNewSessionTitle(title);
                            });
                        });
                    }
                });
                // 请求进行chat
                const response = await fetch(`${DefaultURL}api/chat/`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        query: message, // 发送的 prompt
                        history: historyMsgsRef.current, // 发送的历史记录
                        user: 'point5',
                    }),
                });
                const reader = response.body.getReader();
                abortChatRef.current = () => {
                    reader?.cancel();
                };
                const decoder = new TextDecoder();
                let done = false;
                let result = "";

                while (!done) {
                    const { value, done: doneReading } = await reader.read();
                    done = doneReading;

                    if (value) {
                        const chunk = decoder.decode(value);
                        result += chunk;
                        // console.log(chunk); // 实时输出每个流数据块
                        onUpdate(result); // 更新前端显示
                        // 实时格式化并更新 UI
                        // const formattedResult = formatLLMResponse(result);
                        // onUpdate(formattedResult);
                    }
                }
                // 存储回话
                const message_temp = {
                    id: 0,
                    message: result,
                    status: 'success',
                };
                await addSessionMessages(activeSessionID, message_temp);
                // 设置新的历史消息
                historyMsgsRef.current = [{ 'role': POINT5_USER, 'content': message }, {
                    'role': POINT5_ASSISTANT,
                    'content': result
                }];
                // 请求完成后调用
                onSuccess(result);
            } catch (error) {
                console.error("Error while sending prompt:", error);
                // 存储回话
                const message_temp = {
                    id: 0,
                    message: "请求出错，请稍后再试！",
                    status: 'success',
                };
                await addSessionMessages(activeSessionID, message_temp);
                onError("请求出错，请稍后再试！");
            }
        },
    });

    // Chat messages
    const { onRequest, messages, setMessages } = useXChat({
        agent,
        requestPlaceholder: '思考中......',
    });

    // 更改了session会话，刷新的时候也会运行
    const handleChangeSession = () => {
        console.log(`当前激活的session: ${sessionStorage.getItem(ACTIVE_SESSION_KEY)}`);
        const activeSessionID = sessionStorage.getItem(ACTIVE_SESSION_KEY);
        // 同步建议的suggestions
        getSessionSuggestions(activeSessionID).then((suggestions) => {
            setSuggestions(suggestions);
        });
        getSessionMessages(activeSessionID).then((msgs) => {
            setMessages(msgs);
            // 开始设置历史消息数据
            // 如果长度小于3，证明这还没有进行过完整的对话
            if (msgs.length < 3) {
                historyMsgsRef.current = [];
                return;
            }
            try {
                const lastTwoMsgs = msgs.slice(-2); // 使用 slice 获取最后两项
                historyMsgsRef.current = [
                    { 'role': POINT5_USER, 'content': lastTwoMsgs[0].message },
                    { 'role': POINT5_ASSISTANT, 'content': lastTwoMsgs[1].message },
                ];
            } catch (error) {
                console.log(error);
                historyMsgsRef.current = [];
            }
        });
        // 切换会话，设置为需要滚动
        setWillScroll(true);
    };

    // 发送了query，用于更新questions
    const handleSendQuery = (message) => {
        const activeSessionID = sessionStorage.getItem(ACTIVE_SESSION_KEY);
        // 拓展三个问题
        getQuestions({ query: message }).then((questions) => {
            setSuggestions(questions);
            updateSessionSuggestions(activeSessionID, questions).then();
        });
    };

    return (
        <div style={{
            height: "calc(var(--vh, 1vh) * 100)",
            width: "calc(var(--vw, 1vw) * 100)",
            overflow: "hidden"
        }}>
            <Layout style={{ width: '100%', height: '100%' }}>
                <Sider trigger={null} breakpoint="md" collapsedWidth='0' width='18%' style={{ background: '#F4F4F4' }}
                    onBreakpoint={(broken) => {
                        // 达到需要收起的断点了
                        if (broken === true) {
                            setIsMobile(true);
                        } else {
                            setIsMobile(false);
                        }
                    }}>
                    <SiderMenu newSessionTitle={newSessionTitle} isMobile={false}
                        handleChangeSession={handleChangeSession} />
                </Sider>
                <Layout style={{ background: '#FFFFFF' }}>
                    <Header style={{ padding: 0, background: '#fff', position: 'sticky', top: 0 }}>
                        <HeaderMenu isMobile={isMobile} showDrawer={showDrawer} />
                    </Header>
                    <Content style={{ background: '#fff', flexGrow: 1 }}>
                        <ContentView messages={messages} questions={suggestions} onRequest={onRequest}
                            willScroll={willScroll} setWillScroll={setWillScroll} isMobile={isMobile} setSuggestions={setSuggestions}/>
                    </Content>
                    <Footer style={{ background: '#fff', padding: '24px 10px' }}>
                        <FooterView
                            submit={(nextContent) => {
                                onRequest(nextContent);
                            }}
                            onRequest={onRequest} loading={agent.isRequesting()} handleSendQuery={handleSendQuery}
                            handleChatCancel={() => abortChatRef.current()} />
                    </Footer>
                </Layout>
            </Layout>
            <MobileSiderDrawer drawerOnClose={drawerOnClose} isDrawerOpen={isDrawerOpen}
                handleChangeSession={handleChangeSession} />
        </div>
    )
};

export default MainPage;